// The Steps tab: the unique activities observed across the cases in scope — not the sequence of
// any one case. Its main reading is how many cases contain each step, which separates the core of
// the process from what only happens in some situations; its branches show where cases split.
// A step here is work being performed; the pauses between steps are waits, and live on the Flow
// tab.
import { ChevronRight } from "lucide-react";
import React from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "../../utils";
import { AppSelect } from "../AppSelect";
import DurationHistogram, { type DurationRange } from "./DurationHistogram";
import { StepTypeBars } from "./ProcessStepTypes";
import { stepApps } from "./processAppUsage";
import { type ProcessEdge, type StepTimeStats, stepTableRows } from "./processGraphModel";
import { type Categories, categoryName, hasStepType, stepTypeShares } from "./processGroups";
import { matchFields } from "./processSearch";
import {
  FilterChip,
  KpiTiles,
  PANEL_SELECT_CLASS,
  PanelSearchInput,
  PanelSection,
  SortHeader,
  StepAppIcon,
  TABLE_PAGE_SIZE,
  TablePager,
} from "./processSidebarUi";

type SortColumn = "name" | "cases" | "branches" | "avg" | "total";
type Frequency = "all" | "frequent" | "rare";

// Frequent and rare are relative to the process: the quarter of its steps in the most cases and
// the quarter in the fewest, but no more than ten, so a large process still names a short core.
// Each takes in the steps tied with it, so steps done in every case are all frequent, while a tie
// across the middle joins neither.
const EDGE_SHARE = 0.25;
const EDGE_MAX = 10;

/** Whether a step in this many cases is frequent, rare or neither (undefined). */
const frequencyBands = (counts: number[]) => {
  const sorted = [...counts].sort((a, b) => b - a);
  const edge = Math.max(1, Math.min(EDGE_MAX, Math.ceil(sorted.length * EDGE_SHARE)));
  const most = sorted[0];
  const fewest = sorted[sorted.length - 1];
  const top = sorted[edge - 1];
  const bottom = sorted[sorted.length - edge];
  return (cases: number): Frequency | undefined =>
    cases >= top && (cases > bottom || cases === most)
      ? "frequent"
      : cases <= bottom && (cases < top || cases === fewest)
        ? "rare"
        : undefined;
};

const ALL_APPS = "\u0000all";

// Case readings first — how many cases do the step and where they go next — then its time.
const GRID =
  "grid grid-cols-[minmax(0,1fr)_6.5rem_3.75rem_1rem] items-center gap-x-3 @lg:grid-cols-[minmax(0,1fr)_8rem_5rem_4rem_4.5rem_1rem] @lg:gap-x-4";

const ProcessStepsTab = ({
  caseCount,
  stepStats,
  edges,
  onSelectStep,
  onPreviewSteps,
  categories,
}: {
  /** Cases in scope: the denominator of every "cases containing this step" reading. */
  caseCount: number;
  stepStats: StepTimeStats;
  /** The transitions in scope: each one leaving a step is one of its branches. */
  edges: ProcessEdge[];
  onSelectStep: (stepId: string) => void;
  /** Pointing at a row lights its steps on the map; null lets go. */
  onPreviewSteps: (stepIds: string[] | null) => void;
  /** Names step types; `null` while loading, which hides their section. */
  categories?: Categories | null;
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [app, setApp] = useState(ALL_APPS);
  const [frequency, setFrequency] = useState<Frequency>("all");
  const [durationRange, setDurationRange] = useState<DurationRange | null>(null);
  const [stepType, setStepType] = useState<string | null>(null);
  const [sort, setSort] = useState<{ column: SortColumn; descending: boolean }>({
    column: "total",
    descending: true,
  });
  const [page, setPage] = useState(0);

  const rows = useMemo(() => {
    // One edge per pair of steps, so a step's branches are the edges leaving it: the same next
    // steps, the end of the case and repeats included, that its detail lists.
    const branches = new Map<string, number>();
    for (const edge of edges) branches.set(edge.from, (branches.get(edge.from) ?? 0) + 1);
    return stepTableRows(stepStats.steps).map(row => ({
      ...row,
      apps: stepApps(row.node),
      branches: branches.get(row.node.id) ?? 0,
    }));
  }, [edges, stepStats.steps]);
  const occurrenceDurations = useMemo(
    () =>
      stepStats.steps
        .flatMap(node => node.instances.map(occurrence => occurrence.duration))
        .sort((a, b) => a - b),
    [stepStats.steps],
  );
  const appChoices = useMemo(
    () => [...new Set(rows.flatMap(row => row.apps))].sort((a, b) => a.localeCompare(b)),
    [rows],
  );
  const totalActive = occurrenceDurations.reduce((sum, value) => sum + value, 0);
  const frequencyOf = useMemo(() => frequencyBands(rows.map(row => row.cases)), [rows]);
  // When every step is in as many cases, none is more frequent than another: the filter goes, and
  // a choice made before it went stops applying.
  const showFrequency = rows.some(row => row.cases !== rows[0].cases);
  const stepTypes = useMemo(
    () => stepTypeShares(stepStats.steps.flatMap(node => node.instances)),
    [stepStats.steps],
  );

  const filtered = useMemo(() => {
    const value = (row: (typeof rows)[number]) =>
      sort.column === "avg"
        ? row.avgDuration
        : sort.column === "cases"
          ? row.cases
          : sort.column === "branches"
            ? row.branches
            : row.totalDuration;
    return rows
      .filter(
        row =>
          (app === ALL_APPS || row.apps.includes(app)) &&
          (frequency === "all" || !showFrequency || frequencyOf(row.cases) === frequency) &&
          (!stepType || row.node.instances.some(occurrence => hasStepType(occurrence, stepType))) &&
          (!durationRange ||
            row.node.instances.some(
              occurrence =>
                occurrence.duration >= durationRange.min &&
                occurrence.duration <= durationRange.max,
            )) &&
          (!query.trim() ||
            matchFields(query, [
              ["step", row.node.name],
              ...row.apps.map(name => ["app", name] as ["app", string]),
            ]) !== null),
      )
      .sort((a, b) =>
        sort.column === "name"
          ? (sort.descending ? -1 : 1) * a.node.name.localeCompare(b.node.name)
          : (sort.descending ? -1 : 1) * (value(a) - value(b)) ||
            a.node.name.localeCompare(b.node.name),
      );
  }, [app, durationRange, frequency, frequencyOf, query, rows, showFrequency, sort, stepType]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / TABLE_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(
    currentPage * TABLE_PAGE_SIZE,
    (currentPage + 1) * TABLE_PAGE_SIZE,
  );
  const onSort = (column: SortColumn) => {
    setSort(current => ({
      column,
      descending: current.column === column ? !current.descending : column !== "name",
    }));
    setPage(0);
  };

  return (
    <div className="@container">
      <KpiTiles
        items={[
          { label: t("processPanel.metricUniqueSteps"), value: rows.length },
          {
            label: t("processPanel.metricAvgStepDuration"),
            value: formatDuration(
              occurrenceDurations.length > 0 ? totalActive / occurrenceDurations.length : 0,
            ),
          },
          { label: t("processPanel.metricTotalActive"), value: formatDuration(totalActive) },
          {
            label: t("processPanel.metricStepsPerCase"),
            value: (caseCount > 0 ? stepStats.occurrences / caseCount : 0).toFixed(1),
          },
        ]}
      />

      {categories && stepTypes.length > 1 && (
        <PanelSection
          title={t("processPanel.stepTypeTime")}
          help={t("processPanel.stepTypeTimeHelp")}
        >
          <StepTypeBars
            shares={stepTypes}
            categories={categories}
            onPreview={id =>
              onPreviewSteps(
                id
                  ? stepStats.steps
                      .filter(node =>
                        node.instances.some(occurrence => hasStepType(occurrence, id)),
                      )
                      .map(node => node.id)
                  : null,
              )
            }
            onSelect={id => {
              setStepType(current => (current === id ? null : id));
              setPage(0);
            }}
          />
        </PanelSection>
      )}

      {occurrenceDurations.length >= 3 && (
        <PanelSection
          title={t("processPanel.stepDistribution")}
          help={`${t("processPanel.stepDistributionHelp")} ${t("processPanel.histogramHintSelect")}`}
        >
          <DurationHistogram
            values={occurrenceDurations}
            height={180}
            countLabel={t("occurrences")}
            title={false}
            selection={durationRange}
            onSelectionChange={range => {
              setDurationRange(range);
              setPage(0);
            }}
          />
        </PanelSection>
      )}

      <PanelSection title={t("processPanel.stepList")}>
        <div className="flex flex-wrap items-center gap-2">
          <PanelSearchInput
            value={query}
            onChange={value => {
              setQuery(value);
              setPage(0);
            }}
            label={t("processPanel.searchSteps")}
          />
          {appChoices.length > 1 && (
            <div className={PANEL_SELECT_CLASS}>
              <AppSelect
                contentClassName="pd-theme z-[121]"
                label={t("processPanel.columnApps")}
                value={app}
                onChange={value => {
                  setApp(value);
                  setPage(0);
                }}
                choices={[
                  { id: ALL_APPS, display: t("processPanel.allApps") },
                  ...appChoices.map(name => ({
                    id: name,
                    display: name,
                    icon: <StepAppIcon app={name} fallback="space" />,
                  })),
                ]}
              />
            </div>
          )}
          {showFrequency && (
            <div className={PANEL_SELECT_CLASS}>
              <AppSelect<Frequency>
                contentClassName="pd-theme z-[121]"
                label={t("processPanel.frequencyLabel")}
                value={frequency}
                onChange={value => {
                  setFrequency(value);
                  setPage(0);
                }}
                choices={(["all", "frequent", "rare"] as const).map(id => ({
                  id,
                  display: t(`processPanel.frequency_${id}`),
                }))}
              />
            </div>
          )}
          {categories && stepType && (
            <FilterChip
              label={t("processPanel.stepTypeFilter", {
                type: categoryName(stepType, categories, t),
              })}
              onClear={() => {
                setStepType(null);
                setPage(0);
              }}
            />
          )}
          {durationRange && (
            <FilterChip
              label={t("processPanel.stepRangeFilter", {
                from: formatDuration(durationRange.min),
                to: formatDuration(
                  Number.isFinite(durationRange.max)
                    ? durationRange.max
                    : (occurrenceDurations[occurrenceDurations.length - 1] ?? 0),
                ),
              })}
              onClear={() => {
                setDurationRange(null);
                setPage(0);
              }}
            />
          )}
        </div>

        <div className="mt-3 overflow-hidden rounded-lg border border-outline">
          <div
            className={`${GRID} border-b border-outline bg-surface-strong/60 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-text-dim`}
          >
            <SortHeader
              label={t("processPanel.columnStep")}
              column="name"
              sort={sort}
              onSort={onSort}
            />
            <SortHeader
              label={t("processPanel.columnCasesWithStep")}
              column="cases"
              sort={sort}
              onSort={onSort}
            />
            <span className="hidden @lg:block">
              <SortHeader
                label={t("processPanel.columnBranches")}
                title={t("processPanel.columnBranchesHelp")}
                column="branches"
                align="right"
                sort={sort}
                onSort={onSort}
              />
            </span>
            <SortHeader
              label={t("processPanel.columnAvg")}
              column="avg"
              align="right"
              sort={sort}
              onSort={onSort}
            />
            <span className="hidden @lg:block">
              <SortHeader
                label={t("processPanel.columnTotal")}
                column="total"
                align="right"
                sort={sort}
                onSort={onSort}
              />
            </span>
          </div>
          {visible.length > 0 ? (
            <ul className="divide-y divide-outline" onPointerLeave={() => onPreviewSteps(null)}>
              {visible.map(row => {
                const share = caseCount > 0 ? row.cases / caseCount : 0;
                return (
                  <li key={row.node.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onPreviewSteps(null);
                        onSelectStep(row.node.id);
                      }}
                      onPointerEnter={() => onPreviewSteps([row.node.id])}
                      onFocus={() => onPreviewSteps([row.node.id])}
                      onBlur={() => onPreviewSteps(null)}
                      aria-label={t("processPanel.openStep", { step: row.node.name })}
                      className={`group ${GRID} w-full px-3 py-2.5 text-left text-sm transition hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="flex shrink-0 items-center -space-x-1">
                          {(row.apps.length > 0 ? row.apps.slice(0, 2) : [null]).map(name => (
                            <StepAppIcon key={name ?? ""} app={name} fallback="space" />
                          ))}
                        </span>
                        <span
                          className="min-w-0 truncate font-medium text-text"
                          title={row.node.name}
                        >
                          {row.node.name}
                        </span>
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-baseline justify-between gap-2 text-xs tabular-nums">
                          <span className="text-text-mid">
                            {row.cases}/{caseCount}
                          </span>
                          <span className="text-text-dim">{Math.round(share * 100)}%</span>
                        </span>
                        <span
                          aria-hidden="true"
                          className="mt-1 block h-1.5 overflow-hidden rounded-full bg-app-usage-track"
                        >
                          <span
                            className="block h-full rounded-full bg-app-usage/70"
                            style={{ width: `${Math.max(3, share * 100)}%` }}
                          />
                        </span>
                      </span>
                      <span className="hidden text-right tabular-nums text-text-mid @lg:block">
                        {row.branches}
                      </span>
                      <span className="text-right tabular-nums text-text-mid">
                        {formatDuration(row.avgDuration)}
                      </span>
                      <span className="hidden text-right tabular-nums text-text-mid @lg:block">
                        {formatDuration(row.totalDuration)}
                      </span>
                      <ChevronRight
                        aria-hidden="true"
                        className="h-4 w-4 text-text-dim transition group-hover:text-text"
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="px-3 py-6 text-center text-sm text-text-dim">
              {t("processPanel.noMatchingSteps")}
            </p>
          )}
        </div>
        <TablePager page={currentPage} total={filtered.length} onPageChange={setPage} />
      </PanelSection>
    </div>
  );
};

export default ProcessStepsTab;
